import { useQuery } from '@tanstack/react-query';
import Axios from 'axios';
import {
  plato_api_question_answer_key_html_question_path,
  plato_api_question_stimuli_html_question_path,
} from '@/modules/routes';
import { useQuestionRubric } from '@/components/staffer/ExerciseBuilder/Rubric/useQuestionRubric';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import useSectionTitle from '@/components/staffer/Grading/useSectionTitle';
import { StimuliData } from '@/components/staffer/Grading/utils';
import { IQuestion } from '@/components/staffer/Grading/types';

type RubricData = ReturnType<typeof useQuestionRubric>['data'];

interface UseQuestionCardDataResult {
  answerKeyHtml: string | undefined;
  isLoading: boolean;
  rubricData: RubricData;
  sectionTitle: string;
  stimuli: StimuliData | undefined;
}

// AIDEV-NOTE: Aggregates every query a QuestionCard needs so we can gate the whole card on a
// single isLoading flag — avoids the piecewise in-card flicker where the header, stimuli,
// answer key, and rubric each pop in at different times.
// hasPosition: when the parent supplies a numeric `position` (investigation notebooks, slideshows,
// assessments, lesson review games) the card header shows "Question N" instead of the section
// title, so useSectionTitle's query is disabled entirely.
// answer-key/rubric fetches mirror the render gates in BaseCard/BaseQuestionModeCard — students
// without `show_answers_to_students` (and prompt-only question-mode cards) never render the
// AnswerKey, so we skip the requests and exclude them from isLoading to avoid a phantom skeleton.
const useQuestionCardData = (
  question: IQuestion,
  hasPosition: boolean,
): UseQuestionCardDataResult => {
  const { assignment, isQuestionMode, isTeacher } = useGradingStore();
  const showAnswersToStudents = !!assignment?.show_answers_to_students;
  const willRenderAnswerKey = isQuestionMode
    ? question.type !== 'PromptOnly'
    : (isTeacher || showAnswersToStudents);

  const sectionTitleResult = useSectionTitle({
    enabled: !hasPosition,
    exerciseId: question.question_group.exercise_id,
    exerciseType: question.question_group.exercise_type,
  });

  const stimuliQuery = useQuery<StimuliData>(['questionStimuli', question.id], () => (
    Axios.get(plato_api_question_stimuli_html_question_path(question.id))
      .then(res => res.data)
  ));

  const answerKeyQuery = useQuery<string>(
    ['answerKey', question.id],
    () => (
      Axios.get(plato_api_question_answer_key_html_question_path(question.id))
        .then(res => res.data)
    ),
    { enabled: willRenderAnswerKey },
  );

  const rubricResult = useQuestionRubric({ enabled: willRenderAnswerKey, questionId: question.id });

  const isLoading = sectionTitleResult.isLoading
    || stimuliQuery.isLoading
    || (willRenderAnswerKey && answerKeyQuery.isLoading)
    || (willRenderAnswerKey && rubricResult.isLoading);

  return {
    answerKeyHtml: answerKeyQuery.data,
    isLoading,
    rubricData: rubricResult.data,
    sectionTitle: sectionTitleResult.sectionTitle,
    stimuli: stimuliQuery.data,
  };
};

export default useQuestionCardData;
